import blouseImage from '@/../../public/storage/images/measurement-guides/blouse.jpg';
import lehengaImage from '@/../../public/storage/images/measurement-guides/lehenga-choli.jpg';
import pantImage from '@/../../public/storage/images/measurement-guides/pant.jpg';
import pattupavadaiImage from '@/../../public/storage/images/measurement-guides/pattupavadai.jpg';
import salwaarImage from '@/../../public/storage/images/measurement-guides/salwaar-kameez.jpg';
import sherwaniImage from '@/../../public/storage/images/measurement-guides/sherwani.jpg';
import shirtImage from '@/../../public/storage/images/measurement-guides/shirt.jpg';
import { InputField } from '@/components/FormElements';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import Typography from '@/components/ui/typography';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

const MeasurementGuide = [
  {
    name: 'Blouse',
    image: blouseImage,
  },
  {
    name: 'Pant',
    image: pantImage,
  },
  {
    name: 'Shirt',
    image: shirtImage,
  },
  {
    name: 'Lehenga Choli',
    image: lehengaImage,
  },
  {
    name: 'Pattupavadai',
    image: pattupavadaiImage,
  },
  {
    name: 'Salwaar Kameez',
    image: salwaarImage,
  },
  {
    name: 'Sherwani',
    image: sherwaniImage,
  },
];

const MeasurementDialog = ({
  onResult,
}: {
  onResult: (data: Record<string, string>) => void;
}) => {
  const form = useForm<Record<string, string>>({
    defaultValues: {
      Shoulder: '',
      'Blouse Back Length': '',
      'Chest Round': '',
      'Waist Round': '',
      'Sleeve Length': '',
      'Sleeve Round (Arms, Elbow, Wrist)': '',
      'Full Length': '',
      'Hip Round': '',
      'Front Neck Depth': '',
      'Back Neck Depth': '',
      'Upper Leg Width': '',
      'Lower Leg Width': '',
      'Other Measurements (Specify in Notes)': '',
    },
  });

  const [imgIndex, setImgIndex] = useState(-1);

  return (
    <Dialog>
      <div className="space-y-1">
        <DialogTrigger asChild>
          <Button type="button" variant="outline" className="w-fit">
            <Plus size={16} className="mr-2" /> Add Measurements
          </Button>
        </DialogTrigger>
      </div>
      <DialogContent className="w-11/12 sm:max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Measurements</DialogTitle>
          <DialogDescription>
            Fill the below form to provide us measurements.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-2">
          <div>
            <Typography.Body className="font-semibold">
              Measurement Guide
            </Typography.Body>
            <Typography.Subtle>Tap a guide to enlarge it</Typography.Subtle>
          </div>

          <Carousel className="flex w-full flex-col gap-3">
            <CarouselContent className="-ml-1">
              {MeasurementGuide.map((guide, index) => (
                <CarouselItem key={guide.name} className="basis-28 pl-1">
                  <button
                    type="button"
                    onClick={() => setImgIndex(index)}
                    className="group flex w-full flex-col items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-accent"
                  >
                    <img
                      className="h-28 w-full rounded-lg border object-cover transition-transform group-hover:scale-105"
                      src={guide.image}
                      alt={guide.name}
                      loading="lazy"
                    />
                    <Typography.Small className="text-muted-foreground">
                      {guide.name}
                    </Typography.Small>
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {imgIndex > -1 && (
            <div className="relative overflow-hidden rounded-lg border bg-muted/40">
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="absolute right-2 top-2 size-7 rounded-full shadow-sm"
                aria-label="Close preview"
                onClick={() => setImgIndex(-1)}
              >
                <X size={14} />
              </Button>
              <img
                className="w-full object-contain"
                src={MeasurementGuide[imgIndex].image}
                alt={MeasurementGuide[imgIndex].name}
                loading="lazy"
              />
            </div>
          )}
        </div>
        <Form {...form}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();

              await form.handleSubmit((data) => {
                // Remove fields with empty string values directly on the 'data' object
                for (const key in data) {
                  if (data[key] === '') {
                    delete data[key];
                  }
                }
                onResult(data);
              })();
            }}
            className="pt-6 flex flex-col gap-y-6"
          >
            <Typography.Subtle>
              All Measurements to be in inches
            </Typography.Subtle>
            {Object.keys(form.getValues()).map((field) => (
              <InputField
                key={field}
                control={form.control}
                name={field}
                label={field}
              />
            ))}
            <div className="flex items-center gap-4">
              <DialogClose asChild>
                <Button type="submit" className="w-fit">
                  Save
                </Button>
              </DialogClose>
              <Button
                type="reset"
                variant="outline"
                className="w-fit"
                onClick={() => form.reset()}
              >
                Reset
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default MeasurementDialog;
